// The show view's API shapes (module 10 U149, U158, U160, U161; the brief DEALS-PAGE-BRIEF-2026-09-22.md
// sections 4 item 2 and 8). A module of this track's own, as deals/api.ts is the Deals page's: the
// portal's api.ts is another track's file (infra/agent-guard/OWNERS).
import { viewFetch as fetch } from '../view-as.js';
import { Unauthenticated } from '../api.js';

export class ShowError extends Error {
  constructor(readonly status: number, message: string) { super(message); }
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) throw new ShowError(res.status, `${res.status} ${res.statusText}`);
  return (await res.json()) as T;
}

export interface ShowHead {
  id: string;
  name: string;
  city: string | null;
  venue: string | null;
  stand: string | null;
  startsOn: string;
  endsOn: string;
  timeZone: string;
  sourceLabel: string;
  lastReadOkAt: string | null;
  lastReadError: string | null;
  lastReadAt: string | null;
}

export interface Clash { who: string; otherMeetingId: string }

export interface ShowMeeting {
  id: string;
  kind: 'meeting' | 'presentation' | 'sample';
  startsAt: string | null;
  endsAt: string | null;
  dayLabel: string | null;
  meetingOn: string | null;
  timeText: string | null;
  contactName: string | null;
  contactTitle: string | null;
  companyLabel: string | null;
  organisationId: string | null;
  organisationName: string | null;
  customerOrProspect: string | null;
  products: string[];
  primaryPrincipalId: string | null;
  primaryName: string | null;
  primaryRepText: string | null;
  secondPrincipalId: string | null;
  secondName: string | null;
  secondRepText: string | null;
  deal: { id: string; title: string } | null;
  clashes: Clash[];
  isReaders: boolean;
  /** After the show: sent (the reader can see it), not sent (knowably), or null for nothing to say. */
  followUp: FollowUp | null;
}

export type FollowUp = { kind: 'sent'; at: string; byPrincipalId: string } | { kind: 'not_sent'; hours: number };

export interface ShowTarget {
  id: string;
  list: 'to_reach_out' | 'not_attending';
  companyLabel: string;
  organisationId: string | null;
  organisationName: string | null;
  statusText: string | null;
  contactName: string | null;
  contactTitle: string | null;
}

/** A debrief as the reader may see it (B1703): a colleague's only once its words are theirs to read. */
export interface Debrief {
  id: string;
  meetingId: string | null;
  authorPrincipalId: string;
  authorName: string | null;
  isReaders: boolean;
  state: 'waiting' | 'transcribing' | 'done' | 'failed';
  recordedAt: string;
  words: string | null;
  error: string | null;
}

export interface ShowSheet {
  show: ShowHead;
  now: string;
  teamMeetingCount: number;
  readersCount: number;
  readersNextId: string | null;
  meetings: ShowMeeting[];
  notBooked: ShowTarget[];
  notAttending: ShowTarget[];
  /** Whether debriefs are switched on for this store (SHOW_DEBRIEF); off, no button is drawn. */
  debriefOn: boolean;
  debriefs: Debrief[];
}

export interface CurrentShow extends ShowHead {
  teamMeetingCount: number;
  notBookedCount: number;
  readersNext: Pick<ShowMeeting, 'id' | 'startsAt' | 'timeText' | 'companyLabel' | 'organisationName' | 'contactName' | 'contactTitle' | 'products'> | null;
}

export const showApi = {
  current: (store: string) =>
    fetch(`/api/shows/current?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' })
      .then((r) => json<{ shows: CurrentShow[] }>(r)),
  sheet: (store: string, id: string) =>
    fetch(`/api/shows/${encodeURIComponent(id)}?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' })
      .then((r) => json<ShowSheet>(r)),
  /** The recording as the body, typed as the browser recorded it. */
  debrief: (store: string, id: string, meetingId: string, audio: Blob) =>
    fetch(`/api/shows/${encodeURIComponent(id)}/meetings/${encodeURIComponent(meetingId)}/debrief?store=${encodeURIComponent(store)}`, {
      method: 'POST', credentials: 'same-origin', headers: { 'content-type': audio.type || 'audio/webm' }, body: audio,
    }).then(async (r) => {
      if (r.status === 401) throw new Unauthenticated();
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        throw new ShowError(r.status, j.error ?? `${r.status} ${r.statusText}`);
      }
      return (await r.json()) as { id: string; state: 'waiting' };
    }),
};
